<template>
  <AppHeader />
  <div class="min-h-screen bg-blue-50">
    <!-- Header -->
    <div class="bg-gradient-to-r from-blue-900 to-blue-700 py-8 shadow-md">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex flex-col items-center">
          <div class="text-center">
            <h1 class="text-2xl font-bold text-white mb-1">HustleUP Marketplace</h1>
            <p class="text-blue-100">Buy and sell items with fellow students</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Main content -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
      <!-- Tab Navigation -->
      <div class="bg-white rounded-lg shadow mb-6 p-2">
        <div class="flex gap-2">
          <button
            @click="activeTab = 'browse'"
            :class=" [
              'flex-1 px-4 py-2 rounded-lg font-medium transition-colors',
              activeTab === 'browse' 
                ? 'bg-blue-900 text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            ]"
          >
            Browse All
          </button>
          <button
            @click="activeTab = 'my-listings'"
            :class=" [
              'flex-1 px-4 py-2 rounded-lg font-medium transition-colors',
              activeTab === 'my-listings' 
                ? 'bg-blue-900 text-white' 
                : 'text-gray-700 hover:bg-gray-100'
            ]"
          >
            My Listings
          </button>
        </div>
      </div>

      <!-- Browse Tab -->
      <div v-if="activeTab === 'browse'">
        <!-- Search and Filter -->
        <div class="bg-white rounded-lg shadow mb-6 p-4">
          <div class="flex flex-col md:flex-row gap-4">
            <div class="flex-1">
              <div class="relative">
                <span class="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-500">
                  <span class="material-symbols-outlined">search</span>
                </span>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Search items..."
                  class="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            
            <select
              v-model="selectedCategory"
              class="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
            </select>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center py-12">
          <div class="w-10 h-10 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
        </div>

        <!-- Empty State -->
        <div v-else-if="filteredListings.length === 0" class="bg-white rounded-lg shadow p-8 text-center">
          <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">shopping_bag</span>
          <h3 class="text-lg font-medium text-gray-700 mb-2">No items found</h3>
          <p class="text-gray-500">Be the first to list an item!</p>
        </div>

        <!-- Listings Grid -->
        <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <product-card
            v-for="listing in filteredListings"
            :key="listing.id"
            :listing="listing"
            :current-user-id="user?.uid"
            :show-contact-for-own="true"
            @contact="contactSeller"
          />
        </div>
      </div>

      <!-- My Listings Tab -->
      <div v-if="activeTab === 'my-listings'">
        <div class="bg-white rounded-lg shadow p-6">
          <div class="flex justify-between items-center mb-6">
            <h2 class="text-lg font-semibold text-gray-800">My Listings</h2>
            <button
              v-if="!showAddForm && !editingListing"
              @click="showAddForm = true"
              class="px-4 py-2 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors flex items-center"
            >
              <span class="material-symbols-outlined mr-1">add</span>
              New Listing
            </button>
            <button
              v-else
              @click="cancelForm"
              class="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center"
            >
              <span class="material-symbols-outlined mr-1">close</span>
              Cancel
            </button>
          </div>

          <!-- Add/Edit Listing Form (Inline) -->
          <div v-if="showAddForm || editingListing" class="mb-6 border-b border-gray-200 pb-6">
            <div class="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h3 class="font-medium text-gray-800 mb-4 flex items-center">
                <span class="material-symbols-outlined mr-2">{{ editingListing ? 'edit' : 'add' }}</span>
                {{ editingListing ? 'Update Listing' : 'New Listing' }}
              </h3>
              
              <form @submit.prevent="submitListing">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                    <input
                      v-model="form.title"
                      type="text"
                      required
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Textbook for COS301"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Price (R) *</label>
                    <input
                      v-model.number="form.price"
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Category *</label>
                    <select
                      v-model="form.category"
                      required
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select category</option>
                      <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Condition *</label>
                    <select
                      v-model="form.condition"
                      required
                      class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Select condition</option>
                      <option value="New">New</option>
                      <option value="Like New">Like New</option>
                      <option value="Good">Good</option>
                      <option value="Fair">Fair</option>
                      <option value="Poor">Poor</option>
                    </select>
                  </div>
                </div>

                <div class="mt-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <textarea
                    v-model="form.description"
                    required
                    rows="4"
                    class="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Describe your item..."
                  ></textarea>
                </div>

                <div class="mt-4">
                  <label class="block text-sm font-medium text-gray-700 mb-1">Product Image</label>
                  <div class="mt-1 flex items-center space-x-4">
                    <input 
                      type="file" 
                      ref="fileInput" 
                      accept="image/*" 
                      class="hidden" 
                      @change="handleImageUpload"
                    />
                    <button
                      type="button"
                      @click="selectImage"
                      class="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <span class="material-symbols-outlined mr-2">upload</span>
                      Choose Image
                    </button>
                    <span v-if="selectedImageFile" class="text-sm text-gray-600">
                      {{ selectedImageFile.name }}
                    </span>
                  </div>
                  <p class="text-xs text-gray-500 mt-1">Upload a photo of your item (Max 5MB, JPG/PNG)</p>
                  
                  <!-- Image preview -->
                  <div v-if="imagePreview" class="mt-3 relative">
                    <img 
                      :src="imagePreview" 
                      alt="Product preview" 
                      class="max-h-60 rounded-lg object-contain bg-gray-100 w-full border border-gray-300"
                    />
                    <button 
                      type="button"
                      @click="removeImage" 
                      class="absolute top-2 right-2 bg-gray-800 bg-opacity-70 text-white rounded-full p-1.5 hover:bg-opacity-90 transition-opacity"
                    >
                      <span class="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                </div>

                <div class="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    @click="cancelForm"
                    class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    :disabled="submitting"
                    class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-900 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span v-if="submitting">
                      <span class="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></span>
                      Processing...
                    </span>
                    <span v-else>
                      {{ editingListing ? 'Update Listing' : 'Create Listing' }}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>

          <!-- Loading -->
          <div v-if="loadingMyListings" class="flex justify-center py-8">
            <div class="w-8 h-8 border-4 border-blue-200 border-t-blue-800 rounded-full animate-spin"></div>
          </div>

          <!-- Empty -->
          <div v-else-if="myListings.length === 0 && !showAddForm && !editingListing" class="text-center py-8">
            <span class="material-symbols-outlined text-5xl text-gray-300 mb-3">inventory_2</span>
            <h3 class="text-lg font-medium text-gray-700 mb-2">No listings yet</h3>
            <p class="text-gray-500 mb-4">Start selling by creating your first listing</p>
            <button
              @click="showAddForm = true"
              class="px-6 py-3 bg-blue-900 text-white rounded-lg hover:bg-blue-800 transition-colors inline-flex items-center"
            >
              <span class="material-symbols-outlined mr-2">add</span>
              Create Your First Listing
            </button>
          </div>

          <!-- My Listings Grid -->
          <div v-else-if="myListings.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <product-card
              v-for="listing in myListings"
              :key="listing.id"
              :listing="listing"
              :current-user-id="user?.uid"
              @edit="editListing"
              @delete="confirmDelete"
              @mark-sold="confirmMarkSold"
            />
          </div>
        </div>
      </div>

      <!-- Delete Confirmation (Inline) -->
      <div v-if="listingToDelete" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
          <div class="flex items-start mb-4">
            <span class="material-symbols-outlined text-red-600 text-3xl mr-3">warning</span>
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Delete Listing</h3>
              <p class="text-gray-600 mb-4">Are you sure you want to delete this listing? This action cannot be undone.</p>
              <p class="font-medium text-gray-900">{{ listingToDelete?.title }}</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button
              @click="listingToDelete = null"
              class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="deleteListing"
              class="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <!-- Mark as Sold Confirmation (Inline) -->
      <div v-if="listingToMarkSold" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div class="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
          <div class="flex items-start mb-4">
            <span class="material-symbols-outlined text-green-600 text-3xl mr-3">check_circle</span>
            <div>
              <h3 class="text-lg font-semibold text-gray-900 mb-2">Mark as Sold</h3>
              <p class="text-gray-600 mb-4">Mark this item as sold? This will remove it from active listings.</p>
              <p class="font-medium text-gray-900">{{ listingToMarkSold?.title }}</p>
            </div>
          </div>
          <div class="flex gap-3">
            <button
              @click="listingToMarkSold = null"
              class="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              @click="markListingAsSold"
              class="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              Mark as Sold
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BottomNav />
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue';
import BottomNav from '@/components/common/BottomNav.vue';
import ProductCard from '@/components/marketplace/ProductCard.vue';
import { mapState } from 'vuex';
import {
  getMarketplaceListings,
  getUserListings,
  createListing,
  updateListing,
  deleteListing as deleteListingAPI,
  markAsSold
} from '@/composables/useMarketplace';

export default {
  components: {
    AppHeader,
    BottomNav,
    ProductCard
  },
  data() {
    return {
      activeTab: 'browse',
      listings: [],
      myListings: [],
      loading: false,
      loadingMyListings: false,
      searchQuery: '',
      selectedCategory: 'all',
      showAddForm: false,
      editingListing: null,
      listingToDelete: null,
      listingToMarkSold: null,
      submitting: false,
      categories: ['Books', 'Electronics', 'Clothing', 'Furniture', 'Stationery', 'Other'],
      selectedImageFile: null,
      imagePreview: null,
      uploadingImage: false,
      form: {
        title: '',
        price: 0,
        category: '',
        description: '',
        condition: '',
        imageUrl: ''
      }
    };
  },
  computed: {
    ...mapState(['user', 'profile']),
    
    filteredListings() {
      let filtered = this.listings;
      
      if (this.selectedCategory !== 'all') {
        filtered = filtered.filter(l => l.category === this.selectedCategory);
      }
      
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(l =>
          l.title.toLowerCase().includes(query) ||
          l.description.toLowerCase().includes(query)
        );
      }
      
      return filtered;
    }
  },
  async mounted() {
    await this.loadListings();
  },
  methods: {
    async loadListings() {
      this.loading = true;
      try {
        const result = await getMarketplaceListings();
        if (result.success) {
          this.listings = result.listings;
        }
      } catch (error) {
        console.error('Error loading listings:', error);
      } finally {
        this.loading = false;
      }
    },
    
    async loadMyListings() {
      if (!this.user?.uid) return;
      
      this.loadingMyListings = true;
      try {
        const result = await getUserListings(this.user.uid);
        if (result.success) {
          this.myListings = result.listings;
        }
      } catch (error) {
        console.error('Error loading my listings:', error);
      } finally {
        this.loadingMyListings = false;
      }
    },
    
    selectImage() {
      this.$refs.fileInput.click();
    },
    
    handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // Validate file type
      if (!file.type.match('image.*')) {
        this.$store.dispatch('showAlert', { 
          message: 'Please select an image file (JPG, PNG, etc.)', 
          type: 'error' 
        });
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        this.$store.dispatch('showAlert', { 
          message: 'Image size should be less than 5MB', 
          type: 'error' 
        });
        return;
      }
      
      this.selectedImageFile = file;
      this.imagePreview = URL.createObjectURL(file);
    },
    
    removeImage() {
      this.selectedImageFile = null;
      this.imagePreview = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
      // Don't clear form.imageUrl here if editing - only clear preview
      if (!this.editingListing) {
        this.form.imageUrl = '';
      }
    },
    
    async submitListing() {
      if (!this.user?.uid) return;
      
      this.submitting = true;
      try {
        let imageUrl = this.form.imageUrl || ''; // Keep existing URL if editing
        
        // Upload new image if selected
        if (this.selectedImageFile) {
          this.uploadingImage = true;
          const { uploadImage } = await import('@/composables/useStorage');
          const path = `marketplace/${this.user.uid}/${Date.now()}_${this.selectedImageFile.name}`;
          
          // Upload with compression
          const result = await uploadImage(this.selectedImageFile, path, true);
          
          if (result.success) {
            imageUrl = result.url;
          } else {
            throw new Error('Failed to upload image');
          }
          this.uploadingImage = false;
        }
        
        const listingData = {
          title: this.form.title,
          price: this.form.price,
          category: this.form.category,
          description: this.form.description,
          condition: this.form.condition,
          sellerId: this.user.uid,
          sellerName: this.profile?.name || 'Anonymous',
          sellerProfilePic: this.profile?.profilePicture || '',
          images: imageUrl ? [imageUrl] : []
        };
        
        if (this.editingListing) {
          const result = await updateListing(this.editingListing.id, listingData);
          if (result.success) {
            this.$store.dispatch('showAlert', {
              message: 'Listing updated successfully',
              type: 'success'
            });
            await this.loadMyListings();
            await this.loadListings();
            this.cancelForm();
          }
        } else {
          const result = await createListing(listingData);
          if (result.success) {
            this.$store.dispatch('showAlert', {
              message: 'Listing created successfully',
              type: 'success'
            });
            await this.loadMyListings();
            await this.loadListings();
            this.cancelForm();
          }
        }
      } catch (error) {
        console.error('Error saving listing:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to save listing',
          type: 'error'
        });
      } finally {
        this.submitting = false;
        this.uploadingImage = false;
      }
    },
    
    editListing(listing) {
      this.editingListing = listing;
      this.form = {
        title: listing.title,
        price: listing.price,
        category: listing.category,
        description: listing.description,
        condition: listing.condition,
        imageUrl: listing.images?.[0] || ''
      };
      // Set preview to existing image
      if (listing.images?.[0]) {
        this.imagePreview = listing.images[0];
      }
      this.showAddForm = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    
    confirmDelete(listing) {
      this.listingToDelete = listing;
    },
    
    async deleteListing() {
      if (!this.listingToDelete) return;
      
      try {
        const result = await deleteListingAPI(this.listingToDelete.id, this.user.uid);
        if (result.success) {
          this.$store.dispatch('showAlert', {
            message: 'Listing deleted successfully',
            type: 'success'
          });
          await this.loadMyListings();
          await this.loadListings();
        }
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: 'Failed to delete listing',
          type: 'error'
        });
      } finally {
        this.listingToDelete = null;
      }
    },
    
    confirmMarkSold(listing) {
      this.listingToMarkSold = listing;
    },
    
    async markListingAsSold() {
      if (!this.listingToMarkSold) return;
      
      try {
        const result = await markAsSold(this.listingToMarkSold.id, this.user.uid);
        if (result.success) {
          this.$store.dispatch('showAlert', {
            message: 'Marked as sold!',
            type: 'success'
          });
          await this.loadMyListings();
          await this.loadListings();
        }
      } catch (error) {
        this.$store.dispatch('showAlert', {
          message: 'Failed to update listing',
          type: 'error'
        });
      } finally {
        this.listingToMarkSold = null;
      }
    },
    
    cancelForm() {
      this.showAddForm = false;
      this.editingListing = null;
      this.selectedImageFile = null;
      this.imagePreview = null;
      this.form = {
        title: '',
        price: 0,
        category: '',
        description: '',
        condition: '',
        imageUrl: ''
      };
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    
    async contactSeller(listing) {
      // Check if user is trying to contact themselves
      if (listing.sellerId === this.user?.uid) {
        this.$store.dispatch('showAlert', {
          message: 'You cannot contact yourself. This is your own listing.',
          type: 'info'
        });
        return;
      }
      
      // Navigate to chat with seller
      this.$router.push(`/chat/${listing.sellerId}`);
    }
  },
  watch: {
    activeTab(newTab) {
      if (newTab === 'my-listings') {
        this.loadMyListings();
      }
    }
  }
};
</script>
